/**
 * ROAR-4945 (ROAR-4899 AC4) — how a saved payment method is named in the Payments & Invoices pane.
 *
 * `GET /billing/overview` returns `payment_method: { brand, last4, exp_month, exp_year, type,
 * wallet_type }`. The pane rendered ``${brand} •••• ${last4}`` unconditionally, which is correct
 * only for a plain card: a wallet or bank-account method can carry a null `brand` AND a null
 * `last4`, and that template rendered them literally — "null •••• null" on the billing screen.
 *
 * A registry rather than a branch per method, per CLAUDE.md. Stripe adds payment-method types
 * routinely, and an unregistered one must degrade to something readable rather than to nulls.
 */

/** Stripe `wallet_type` values, which take precedence over the card brand when present. */
export const PAYMENT_WALLET_LABELS = Object.freeze({
  apple_pay: 'Apple Pay',
  google_pay: 'Google Pay',
  samsung_pay: 'Samsung Pay',
  link: 'Link',
  amex_express_checkout: 'Amex Express Checkout',
  masterpass: 'Masterpass',
  visa_checkout: 'Visa Checkout',
});

/** Stripe `type` values — the fallback when there is neither a wallet nor a card brand. */
export const PAYMENT_METHOD_TYPE_LABELS = Object.freeze({
  card: 'Card',
  us_bank_account: 'Bank account',
  sepa_debit: 'SEPA Direct Debit',
  link: 'Link',
});

/** What the pane shows when a method carries nothing nameable at all. */
const FALLBACK_LABEL = 'Saved payment method';

/**
 * Humanises an unregistered value: `us_bank_account` → `Us bank account`.
 *
 * @param {string} value A trimmed, non-empty wire value.
 * @returns {string}
 */
/**
 * An OWN-property lookup.
 *
 * `LABELS[key]` would resolve inherited `Object.prototype` members, so a value of `'constructor'`
 * or `'toString'` returns a FUNCTION rather than a label — which React then refuses to render
 * ("Functions are not valid as a React child"). These registries exist to be defensive about
 * unknown backend values, so the lookup has to actually be.
 *
 * @param {object} registry A frozen label map.
 * @param {string} key A trimmed wire value.
 * @returns {string|undefined}
 */
const lookup = (registry, key) =>
  Object.prototype.hasOwnProperty.call(registry, key) ? registry[key] : undefined;

const humanise = (value) => {
  const spaced = value.replace(/_/g, ' ').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * The display name for a payment method, most specific source first.
 *
 * Order is load-bearing: a card added through Apple Pay carries BOTH a `wallet_type` and the
 * underlying card's `brand`, and the wallet is what the user recognises as the thing they pay
 * with — naming it "Visa" would describe a card they never see.
 *
 * @param {object} [paymentMethod] The overview's `payment_method`.
 * @returns {string} Always a non-empty, human-readable name.
 */
export const resolvePaymentMethodLabel = (paymentMethod) => {
  const wallet = String(paymentMethod?.wallet_type ?? '').trim();
  if (wallet) return lookup(PAYMENT_WALLET_LABELS, wallet) ?? humanise(wallet);

  // Uppercased here rather than by a CSS class on the consumer: the class would also shout the
  // designed labels above it ("APPLE PAY"), and a brand token is the only value that wants it.
  const brand = String(paymentMethod?.brand ?? '').trim();
  if (brand) return brand.toUpperCase();

  const type = String(paymentMethod?.type ?? '').trim();
  if (type) return lookup(PAYMENT_METHOD_TYPE_LABELS, type) ?? humanise(type);

  // Something is on file — the read returned an object — but nothing in it is nameable. Saying so
  // beats rendering an empty line that reads as a failed load.
  return FALLBACK_LABEL;
};

/**
 * Whether the masked-digits suffix should render.
 *
 * Only `last4` gates this, never `brand`: a wallet-funded card legitimately has digits but no
 * brand, and the "•••• 4242" suffix is still the useful half of the line.
 *
 * @param {object} [paymentMethod] The overview's `payment_method`.
 * @returns {boolean}
 */
export const hasMaskedDigits = (paymentMethod) =>
  String(paymentMethod?.last4 ?? '').trim().length > 0;

/**
 * The card-network artwork for a brand token, by NORMALISED brand.
 *
 * Keyed off `brand` alone, never `wallet_type`: the label names the wallet because that is what
 * the user taps to pay, but the artwork names the network because that is the logo printed on the
 * thing being charged — an Apple Pay row funded by a Visa still shows the Visa mark.
 */
export const PAYMENT_BRAND_ICONS = Object.freeze({
  visa: '/images/visacard.svg',
  mastercard: '/images/mastercard.svg',
  amex: '/images/amexcard.svg',
  discover: '/images/discovercard.svg',
});

/** Shown for every method with no artwork of its own — an unmapped network, a wallet, a bank. */
export const PAYMENT_PLACEHOLDER_ICON = '/images/placeholdercard.svg';

/**
 * Normalises a brand token to a registry key.
 *
 * Stripe's canonical tokens are lowercase and underscore-free (`visa`, `mastercard`, `amex`,
 * `discover`), but the same networks reach us spelled out from other sources — "American Express",
 * "Master Card", `master_card`. Stripping everything but letters and digits collapses all of those
 * onto one key, so a cosmetic difference in the wire value cannot silently fall through to the
 * placeholder.
 *
 * @param {string} brand Raw `brand` from the overview payload.
 * @returns {string} A lowercase alphanumeric key, possibly empty.
 */
const normaliseBrand = (brand) =>
  String(brand ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

/** Spellings that normalise to something other than their Stripe token. */
const BRAND_ALIASES = Object.freeze({
  americanexpress: 'amex',
  discovercard: 'discover',
});

/**
 * The artwork for a payment method. Always returns a path — the placeholder when the brand is
 * absent or unmapped, so the row's layout never shifts between a known and an unknown card.
 *
 * @param {object} [paymentMethod] The overview's `payment_method`.
 * @returns {string} A `public/images` path.
 */
export const resolvePaymentMethodIcon = (paymentMethod) => {
  const key = normaliseBrand(paymentMethod?.brand);
  if (!key) return PAYMENT_PLACEHOLDER_ICON;
  const canonical = lookup(BRAND_ALIASES, key) ?? key;
  return lookup(PAYMENT_BRAND_ICONS, canonical) ?? PAYMENT_PLACEHOLDER_ICON;
};

/**
 * The full masked number for display: `•••• •••• •••• 1234`.
 *
 * ROAR-4899: the pane showed only one masked group, which reads as a 8-digit number rather than as
 * a card. All sixteen positions are drawn so the row matches the card in the user's hand.
 *
 * Only the first three groups are hard-coded; `last4` is whatever Stripe stored, so a method with
 * fewer than four digits on file still renders exactly what is known rather than padding a lie.
 *
 * @param {object} [paymentMethod] The overview's `payment_method`.
 * @returns {string} The masked number — call only when `hasMaskedDigits` is true.
 */
export const formatMaskedCardNumber = (paymentMethod) =>
  `•••• •••• •••• ${String(paymentMethod?.last4 ?? '').trim()}`;
